import { ViewModelStore } from '../state';
import { ChangeDetectionStrategy, Component, inject, type OnDestroy } from '@angular/core';
import { environment } from '@rdlabo/shared/src/environments/environment';
import { HttpClient } from '@angular/common/http';
import type { IAppLaunchConstant } from '@rdlabo/odss-mobile-hono/src/api/types';
import type { IUserStatus } from '@rdlabo/odss-mobile-hono/src/api/auth/api';
import { HelperService } from '@rdlabo/shared/src/lib/services/helper.service';
import { IonApp, IonRouterOutlet } from '@ionic/angular';
import { AuthService } from '../../../shared/src/auth/auth.service';
import {
  catchError,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  map,
  of,
  Subscription,
  switchMap,
  take,
  tap,
  type Observable,
} from 'rxjs';
import { defaultUserStatus } from '../reducer/status';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  imports: [IonApp, IonRouterOutlet],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnDestroy {
  readonly vm = new ViewModel(this);
  readonly #userStatusSubscription = new Subscription();

  constructor() {
    this.vm.initializeApp().catch(() => undefined);
    this.#userStatusSubscription.add(this.vm.userStatus$.subscribe());
    if (window.navigator && navigator.serviceWorker) {
      navigator.serviceWorker.getRegistrations().then(function (registrations) {
        for (const registration of registrations) {
          registration.unregister();
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.#userStatusSubscription.unsubscribe();
  }
}

class ViewModel extends ViewModelStore<AppComponent> {
  readonly #http = inject(HttpClient);
  readonly #helper = inject(HelperService);
  readonly #auth = inject(AuthService);
  readonly userStatus$: Observable<IUserStatus> = this.#auth.getState$().pipe(
    map((user) => (user?.emailVerified ? user.uid : undefined)),
    distinctUntilChanged(),
    switchMap((uid) =>
      uid
        ? this.#http.get<IUserStatus>(environment.api() + 'auth/status').pipe(catchError(() => of(defaultUserStatus())))
        : of(defaultUserStatus()),
    ),
    tap((status) => this.setUserStatus(status)),
  );

  async initializeApp(): Promise<void> {
    await firstValueFrom(
      this.#auth.getState$().pipe(
        filter((user) => Boolean(user?.emailVerified)),
        take(1),
      ),
    );
    const constant = await firstValueFrom(this.#http.get<IAppLaunchConstant>(environment.api()));
    this.#helper.setConstant(constant);
  }
}
